import { useLocation, useParams } from 'quartz-routing/react-router';
import { useEffect, useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { ButtonGroup } from 'foundations-components/transitional/components/ButtonGroup';
import { useTheme } from 'foundations-theming/utils';
import ACSkeletonBox from 'academy-common-lib/components/loading/ACSkeletonBox';
import { ACRedirect } from 'academy-common-lib/components/ACRedirect';
import { ACRouterButton } from 'academy-common-lib/components/ACRouterButton';
import ApiError from '../../../components/Errors/ApiError';
import ErrorBoundary from '../../../components/Errors/ErrorBoundary';
import getLang from '../../../utils/getLang';
import UsageTracker from '../../../utils/UsageTracker';
import MyTeamCertificationProfileActiveTab from '../components/certificationProfile/CertificationProfileActiveTab';
import MyTeamCertificationProfileExpiredTab from '../components/certificationProfile/CertificationProfileExpiredTab';
import MyTeamCertificationProfileInProgressTab from '../components/certificationProfile/CertificationProfileInProgressTab';
import Card from '../components/common/Card';
import RoundedCertificationBadge from '../components/RoundedCertificationBadge';
import { usePageTitle } from '../../../hooks/usePageTitle.legacy';
import { PAGE_NAMES } from '../../../constants/Navigation.legacy';
import { FiltersCard } from 'academy-common-lib/features/filters/components/FiltersCard';
import { PageHeader } from 'academy-common-lib/features/header/components/PageHeader';
import { getBreadcrumbItem } from '../constants/breadcrumbs';
import { CERTIFICATIONS_PROFILE_BASE } from '../constants/routes';
import { useCertificationInfo } from '../myTeamQueries';
import { removePageParam } from '../utils/paginationUtils';
import { trackCertificationStatusChange } from '../utils/trackingUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const RedirectToCertificationProfileDefaultTab = () => {
  const location = useLocation();
  const {
    pathname
  } = location;
  return /*#__PURE__*/_jsx(ACRedirect, {
    to: Object.assign({}, location, {
      pathname: `${pathname}/active`
    })
  });
};
const MyTeamCertificationProfile = () => {
  var _certificationInfo$ce;
  const {
    certificationId,
    certificationStatus
  } = useParams();
  const location = useLocation();
  const {
    certificationInfo,
    certificationInfoError
  } = useCertificationInfo(Number(certificationId), getLang());
  const breadcrumbs = useMemo(() => [getBreadcrumbItem({
    pageKey: 'MY_TEAM'
  }), getBreadcrumbItem({
    pageKey: 'MY_TEAM_CERTIFICATIONS'
  }), {
    label: certificationInfo === null || certificationInfo === void 0 ? void 0 : certificationInfo.certificationName,
    isLoading: !(certificationInfo !== null && certificationInfo !== void 0 && certificationInfo.certificationName)
  }], [certificationInfo === null || certificationInfo === void 0 ? void 0 : certificationInfo.certificationName]);
  usePageTitle({
    i18nKey: 'learningCenter.dynamicPageTitles.certificationName',
    options: {
      certificationName: certificationInfo === null || certificationInfo === void 0 ? void 0 : certificationInfo.certificationName
    },
    skip: !(certificationInfo !== null && certificationInfo !== void 0 && certificationInfo.certificationName)
  });
  useEffect(() => {
    UsageTracker.track('pageView', {
      screen: PAGE_NAMES.MY_TEAM_CERTIFICATION_PROFILE
    });
  }, []);
  useEffect(() => {
    trackCertificationStatusChange(certificationStatus);
  }, [certificationStatus]);
  const theme = useTheme();
  if (certificationInfoError) {
    return /*#__PURE__*/_jsx(ApiError, {
      error: {
        status: 500
      },
      markerDefault: "CERTIFICATION_INFO_API_ERROR"
    });
  }
  const certificationProfileUrlFragment = `${CERTIFICATIONS_PROFILE_BASE}/${certificationId}`;
  return /*#__PURE__*/_jsxs(View, {
    children: [/*#__PURE__*/_jsx(PageHeader, {
      breadcrumbs: breadcrumbs,
      subtitle: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.heading.headingSubTitles.MY_TEAM_CERTIFICATION_PROFILE"
      }),
      title: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "500",
        children: [(certificationInfo === null || certificationInfo === void 0 ? void 0 : certificationInfo.icon) && /*#__PURE__*/_jsx(RoundedCertificationBadge, {
          iconUrl: certificationInfo.icon,
          size: "1000"
        }), (_certificationInfo$ce = certificationInfo === null || certificationInfo === void 0 ? void 0 : certificationInfo.certificationName) !== null && _certificationInfo$ce !== void 0 ? _certificationInfo$ce : /*#__PURE__*/_jsx(ACSkeletonBox, {
          animated: true,
          backgroundcolor: theme.color['fill-surface-recessed-hover'],
          "data-test-id": "header-text-loading-skeleton",
          shimmercolor: theme.color['fill-surface-recessed'],
          style: {
            display: 'inline-block',
            height: '24px',
            width: '300px'
          }
        })]
      })
    }), /*#__PURE__*/_jsx(FiltersCard, {
      paddingBlock: "400",
      paddingInline: "400",
      children: /*#__PURE__*/_jsxs(ButtonGroup, {
        children: [/*#__PURE__*/_jsx(ACRouterButton, {
          active: certificationStatus === 'active',
          replace: true,
          to: {
            pathname: `${certificationProfileUrlFragment}/active`,
            search: removePageParam(location.search)
          },
          use: "tertiary-extra-light",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.myTeam.certificationStatus.active"
          })
        }), /*#__PURE__*/_jsx(ACRouterButton, {
          active: certificationStatus === 'expired',
          replace: true,
          to: {
            pathname: `${certificationProfileUrlFragment}/expired`,
            search: removePageParam(location.search)
          },
          use: "tertiary-extra-light",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.myTeam.certificationStatus.expired"
          })
        }), /*#__PURE__*/_jsx(ACRouterButton, {
          active: certificationStatus === 'in-progress',
          replace: true,
          to: {
            pathname: `${certificationProfileUrlFragment}/in-progress`,
            search: removePageParam(location.search)
          },
          use: "tertiary-extra-light",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.myTeam.certificationStatus.inProgress"
          })
        })]
      })
    }), /*#__PURE__*/_jsxs(Card, {
      children: [certificationStatus === 'active' && /*#__PURE__*/_jsx(ErrorBoundary, {
        boundaryName: "CertificationProfileActiveTab",
        children: /*#__PURE__*/_jsx(MyTeamCertificationProfileActiveTab, {
          certificationName: certificationInfo === null || certificationInfo === void 0 ? void 0 : certificationInfo.certificationName
        })
      }), certificationStatus === 'expired' && /*#__PURE__*/_jsx(ErrorBoundary, {
        boundaryName: "CertificationProfileExpiredTab",
        children: /*#__PURE__*/_jsx(MyTeamCertificationProfileExpiredTab, {})
      }), certificationStatus === 'in-progress' && /*#__PURE__*/_jsx(ErrorBoundary, {
        boundaryName: "CertificationProfileInProgressTab",
        children: /*#__PURE__*/_jsx(MyTeamCertificationProfileInProgressTab, {})
      })]
    })]
  });
};
export default MyTeamCertificationProfile;